import { CSSProperties, Fragment, ReactNode, RefObject, useRef } from 'react'

import * as S from './linked-columns.styled'
import { buildLinkShape } from './linked-columns.utils'
import { LinkMeasure, useLinkMeasure } from './use-link-measure'

interface Link {
  from: string
  to: string
}

interface LinkedColumnsProps {
  children: ReactNode
  dimmed?: boolean
  links: Link[]
}

const DOT_RADIUS = 3

const getIndexStyle = (index: number) => ({ '--index': index }) as CSSProperties

const BracketShape = ({ measure }: { measure: LinkMeasure }) => {
  const shape = buildLinkShape(measure)
  if (!shape) return null

  return (
    <>
      <S.Spine d={shape.path} pathLength={1} />
      <S.Fading>
        <circle cx={shape.origin.x} cy={shape.origin.y} r={DOT_RADIUS} />
      </S.Fading>
      {shape.arms.map((arm, index) => (
        <S.Fading key={index} style={getIndexStyle(index)}>
          <path d={arm} />
        </S.Fading>
      ))}
      {shape.dots.map(({ x, y }, index) => (
        <S.Fading key={index} style={getIndexStyle(index)}>
          <circle cx={x} cy={y} r={DOT_RADIUS} />
        </S.Fading>
      ))}
    </>
  )
}

const LinkConnector = ({
  containerRef,
  dimmed,
  from,
  to,
}: Link & {
  containerRef: RefObject<HTMLElement | null>
  dimmed: boolean
}) => {
  const measure = useLinkMeasure(containerRef, from, to)
  if (!measure) return null

  return (
    <S.Overlay aria-hidden $dimmed={dimmed}>
      <Fragment key={measure.parentKey}>
        <BracketShape measure={measure} />
      </Fragment>
    </S.Overlay>
  )
}

export const LinkedColumns = ({ children, dimmed = false, links }: LinkedColumnsProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  return (
    <S.Columns ref={containerRef}>
      {children}
      {links.map((link) => (
        <LinkConnector
          key={`${link.from}-${link.to}`}
          containerRef={containerRef}
          dimmed={dimmed}
          {...link}
        />
      ))}
    </S.Columns>
  )
}
